import { AlertTriangle, Trash2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

interface ExcluirProdutoModalProps {
  produto: { id: string; nome: string; codigo: string } | null;
  onClose: () => void;
  onExcluido: (mensagem: string) => void;
}

export function ExcluirProdutoModal({ produto, onClose, onExcluido }: ExcluirProdutoModalProps) {
  const utils = trpc.useUtils();

  // Só consulta com o modal aberto: é o que decide se a peça vai ser apagada
  // ou desativada, e a resposta precisa ser fresca.
  const { data: check, isLoading } = trpc.produtos.verificarExclusao.useQuery(
    { id: produto?.id ?? '' },
    { enabled: Boolean(produto), staleTime: 0, gcTime: 0 }
  );

  const deleteMutation = trpc.produtos.delete.useMutation({
    onSuccess: (resultado) => {
      utils.produtos.list.invalidate();
      utils.produtos.listCategorias.invalidate();
      onExcluido(resultado.message);
      onClose();
    },
  });

  const podeApagar = check?.podeApagar ?? false;

  const historico = check
    ? [
        check.vendas > 0 && `${check.vendas} ${check.vendas === 1 ? 'venda' : 'vendas'}`,
        check.movimentacoes > 0 &&
          `${check.movimentacoes} ${check.movimentacoes === 1 ? 'movimentação' : 'movimentações'} de estoque`,
      ]
        .filter((parte): parte is string => Boolean(parte))
        .join(' e ')
    : '';

  return (
    <Modal
      isOpen={Boolean(produto)}
      onClose={onClose}
      title={podeApagar ? 'Apagar peça' : 'Excluir peça'}
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-gray-50 p-4">
          <p className="font-medium text-gray-900">{produto?.nome}</p>
          <p className="font-mono text-sm text-gray-500">{produto?.codigo}</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="border-primary-600 h-6 w-6 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        ) : podeApagar ? (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Esta peça não tem vendas nem movimentações. Ela será{' '}
              <strong>apagada em definitivo</strong> e não há como desfazer.
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Esta peça tem {historico}. Ela será <strong>desativada</strong>, não apagada, para não
              furar o histórico de caixa. Sai do PDV e da listagem, mas as vendas antigas continuam
              consultáveis — e dá para reativar depois.
            </span>
          </div>
        )}

        {deleteMutation.isError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{deleteMutation.error.message}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            isLoading={deleteMutation.isPending}
            disabled={isLoading || !produto}
            onClick={() => produto && deleteMutation.mutate({ id: produto.id })}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {podeApagar ? 'Apagar' : 'Desativar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
